/**
 * <ProgramTimeline> — DSH-062 horizontal program-progress visualisation.
 *
 * Renders 10 step markers (Step 0..9) + a graduation marker + an
 * optional 90-day check-in milestone. Tone-coded per status:
 *   - completed → flame
 *   - current   → ink (heavier weight)
 *   - future    → bone-3 (muted)
 *
 * Phase gates (steps 3 + 6) get a small badge; buffer weeks get a
 * dotted border on the step. Reused by:
 *   - the client `/dashboard` route (own timeline)
 *   - the coach `/clients/:clientId/timeline` route (any client)
 *
 * No external dep — inline SVG line + circle markers with brand colors.
 */

import { useTranslation } from 'react-i18next';

import type { ProgramTimeline as ProgramTimelineData, TimelineEntry } from '@/lib/api-client';

interface Props {
  data: ProgramTimelineData;
}

export function ProgramTimeline({ data }: Props): JSX.Element {
  const { t } = useTranslation();
  const stepEntries = data.entries.filter(
    (e): e is Extract<TimelineEntry, { kind: 'step' }> => e.kind === 'step',
  );
  const graduation = data.entries.find((e) => e.kind === 'graduation');
  const milestone = data.entries.find((e) => e.kind === 'milestone');

  return (
    <article
      data-testid="program-timeline"
      className="bg-bone-2 rounded-lg py-s5 border border-bone-3 shadow-sm overflow-x-auto"
    >
      <header className="mb-s4 px-s5">
        <h2 className="font-display text-display-sm text-ink">
          {t('timeline.title')}
        </h2>
        <p className="text-sm text-gray-2 mt-s1">
          {data.projectedGraduation
            ? t('timeline.projectedGraduation', { date: formatDate(data.projectedGraduation) })
            : t('timeline.noGraduation')}
        </p>
      </header>

      <div className="flex items-center gap-s2 min-w-[800px] px-s5" role="list">
        {stepEntries.map((step, idx) => (
          <StepMarker
            key={step.stepNumber}
            entry={step}
            isLast={idx === stepEntries.length - 1}
          />
        ))}
        {graduation && graduation.kind === 'graduation' && (
          <>
            <Connector status={graduation.status} />
            <GraduationMarker entry={graduation} />
          </>
        )}
        {milestone && milestone.kind === 'milestone' && (
          <>
            <Connector status={milestone.status} />
            <MilestoneMarker entry={milestone} />
          </>
        )}
      </div>
    </article>
  );
}

function StepMarker({
  entry,
  isLast,
}: {
  entry: Extract<TimelineEntry, { kind: 'step' }>;
  isLast: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  // F-016 (2026-05-22): completed = semantic-success (green) instead of
  // flame; active = flame-bordered circle (was ink border). Future stays
  // muted bone-3 outline.
  const dotClass = entry.status === 'completed'
    ? 'bg-semantic-success border-semantic-success'
    : entry.status === 'current'
      ? 'bg-bone-2 border-flame border-[3px]'
      : 'bg-bone-2 border-bone-3';
  return (
    <>
      <div role="listitem" className="flex flex-col items-center gap-s1 min-w-[64px]">
        <div
          aria-hidden
          className={`w-7 h-7 rounded-full border-2 flex items-center justify-center text-xs font-medium ${dotClass} ${entry.hasBufferWeek ? 'ring-2 ring-flame ring-offset-1 ring-offset-bone-2' : ''}`}
        >
          <span className={entry.status === 'completed' ? 'text-bone font-semibold' : 'text-ink'}>
            {entry.stepNumber}
          </span>
        </div>
        <span
          className={`text-xs ${entry.status === 'current' ? 'text-ink font-medium' : 'text-gray-2'}`}
        >
          {t(`timeline.steps.${entry.stepNumber}`)}
        </span>
        {entry.isPhaseGate && (
          <span className="text-[10px] uppercase font-mono text-flame-deep">
            {entry.phaseGateOutcome
              ? t(`timeline.phaseGate.${entry.phaseGateOutcome}`)
              : t('timeline.phaseGate.pending')}
          </span>
        )}
      </div>
      {!isLast && <Connector status={entry.status} />}
    </>
  );
}

function Connector({ status }: { status: 'completed' | 'current' | 'future' }): JSX.Element {
  // F-016: green connector between done steps, muted bone-3 otherwise.
  const cls = status === 'completed' ? 'bg-semantic-success' : 'bg-bone-3';
  return <span aria-hidden className={`flex-1 h-[2px] min-w-[20px] ${cls}`} />;
}

function GraduationMarker({
  entry,
}: {
  entry: Extract<TimelineEntry, { kind: 'graduation' }>;
}): JSX.Element {
  const { t } = useTranslation();
  const cls = entry.status === 'completed' ? 'bg-semantic-success border-semantic-success text-bone' : 'bg-bone-2 border-bone-3 text-ink';
  return (
    <div role="listitem" className="flex flex-col items-center gap-s1 min-w-[80px]">
      <div
        aria-hidden
        className={`w-7 h-7 rounded-full border-2 flex items-center justify-center text-xs ${cls}`}
      >
        ★
      </div>
      <span className="text-xs text-ink font-medium">{t('timeline.graduation')}</span>
      {entry.actualOn && (
        <span className="text-[10px] font-mono text-gray-2">{formatDate(entry.actualOn)}</span>
      )}
      {entry.projectedOn && !entry.actualOn && (
        <span className="text-[10px] font-mono text-gray-2">~{formatDate(entry.projectedOn)}</span>
      )}
    </div>
  );
}

function MilestoneMarker({
  entry,
}: {
  entry: Extract<TimelineEntry, { kind: 'milestone' }>;
}): JSX.Element {
  const { t } = useTranslation();
  const cls = entry.status === 'completed'
    ? 'bg-semantic-success border-semantic-success text-bone'
    : entry.status === 'current'
      ? 'bg-bone-2 border-flame border-[3px] text-ink'
      : 'bg-bone-2 border-bone-3 text-ink';
  return (
    <div role="listitem" className="flex flex-col items-center gap-s1 min-w-[100px]">
      <div
        aria-hidden
        className={`w-7 h-7 rounded-full border-2 flex items-center justify-center text-xs ${cls}`}
      >
        90d
      </div>
      <span className="text-xs text-ink font-medium">{t('timeline.ninetyDay')}</span>
      <span className="text-[10px] font-mono text-gray-2">
        {entry.daysUntil >= 0
          ? t('timeline.daysAway', { days: entry.daysUntil })
          : t('timeline.daysAgo', { days: Math.abs(entry.daysUntil) })}
      </span>
    </div>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' });
}
